import { useEffect, useMemo, useState } from "react";

import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Circle,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useSdkKeys } from "@/features/sdk-keys/api";
import { useKeyConfig } from "@/features/simulator/api";
import { useTimezone } from "@/features/timezone/api";
import { useWebhook } from "@/features/webhooks/api";
import { useApp } from "@rook/shared/lib/appContext";
import { Button } from "@rook/ui";

import { SIDEBAR_TOUR_ID } from "./sidebarTour";
import { localStorageTourPersistence } from "./tourPersistence";

/**
 * Floating onboarding checklist (CPF-UIR-112 — added 2026-05-25 after
 * Pepe asked for the Claude Platform-style card).
 *
 * Position: bottom-LEFT. The Intercom chat bubble lives bottom-right;
 * putting the checklist on the opposite side stops them from fighting
 * for the same corner on every viewport size.
 *
 * Single source of truth: every checkable item is derived from the
 * SAME hooks the sidebar `<NavAttentionDot>` reads from (`useWebhook`,
 * `useTimezone`, `useSdkKeys`, `useKeyConfig`). So when the dot
 * clears, the checklist item completes — no two-source drift.
 *
 * Auto-dismiss policy:
 *   - All items complete → card hides itself + leaves a localStorage
 *     flag so it doesn't reappear after a refresh.
 *   - User clicks the X → same flag is set, card hides until a NEW
 *     item appears as pending (e.g. backend rolls out a feature flag
 *     that adds a step). The flag stores the set of completed items
 *     at dismiss-time; reappearance is gated on the set growing OR
 *     a new item being added in code.
 *   - Minimised state (collapsed body, header still visible) is
 *     separate from dismissal and uses its own localStorage key.
 *
 * NOT shown on:
 *   - Auth shell (LoginView, SignupView, etc.) — those routes are
 *     guest-only; the checklist depends on authenticated hooks.
 *   - First mount before any hook resolves (suppresses a flash of
 *     "0/5 complete" while data is still in flight).
 */

const DISMISSED_KEY = "rook-portal:onboarding:dismissed-v1";
const MINIMISED_KEY = "rook-portal:onboarding:minimised-v1";

type ChecklistItem = {
  id: string;
  title: string;
  description: string;
  done: boolean;
  /** Route to navigate to when the row is clicked. */
  to: string;
};

const useChecklistItems = (): { items: ChecklistItem[]; loading: boolean } => {
  const { env } = useApp();
  const webhook = useWebhook(env);
  const timezone = useTimezone(env);
  const sdk = useSdkKeys(env);
  const keyConfig = useKeyConfig(env);

  // Tour completion is async-read once at mount + re-checked when
  // the user navigates somewhere new (cheap cache hit). The signal
  // updates the next time the checklist re-renders.
  const [tourDone, setTourDone] = useState(false);
  useEffect(() => {
    const persistence = localStorageTourPersistence();
    persistence
      .isCompleted(SIDEBAR_TOUR_ID)
      .then(setTourDone)
      .catch(() => setTourDone(false));
  }, []);

  const items = useMemo<ChecklistItem[]>(
    () => [
      {
        id: "webhook",
        title: "Configure webhook endpoint",
        description: "Where ROOK delivers processed health documents.",
        done: !!webhook.data && (webhook.data.endpoint ?? "") !== "",
        to: "/home/delivery/webhooks",
      },
      {
        id: "timezone",
        title: "Set your timezone",
        description: "Default UTC works; your local timezone improves sleep summaries.",
        done: !!timezone.data && (timezone.data.time_zone ?? "UTC") !== "UTC",
        to: "/home/extraction/timezone",
      },
      {
        id: "sdk",
        title: "Register SDK platforms",
        description: "Only if you use the mobile SDK. Skip for API-only integrations.",
        done:
          !!sdk.data &&
          ((sdk.data.bundle_id ?? "") !== "" ||
            (sdk.data.package_name ?? "") !== ""),
        to: "/home/auth/sdk",
      },
      {
        id: "data-structure",
        title: "Customize data structure",
        description: "Pick which event keys ROOK delivers vs. drops.",
        done: keyConfig.data !== null && keyConfig.data !== undefined,
        to: "/home/delivery/data-structure",
      },
      {
        id: "tour",
        title: "Take the guided tour",
        description: "Quick walk through every section of the portal.",
        done: tourDone,
        // Same pattern AccountView's "Take tour again" uses:
        // navigate with ?tour=restart so HomeShell's
        // <SidebarTourMount autostart=...> picks it up.
        to: "/home?tour=restart",
      },
    ],
    [webhook.data, timezone.data, sdk.data, keyConfig.data, tourDone],
  );

  const loading =
    webhook.isLoading ||
    timezone.isLoading ||
    sdk.isLoading ||
    keyConfig.isLoading;

  return { items, loading };
};

const readDismissed = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeDismissed = (completedIds: string[]): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(completedIds));
  } catch {
    /* private browsing / quota — best-effort, don't break UI */
  }
};

const readMinimised = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(MINIMISED_KEY) === "1";
};

const writeMinimised = (value: boolean): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MINIMISED_KEY, value ? "1" : "0");
  } catch {
    /* swallow */
  }
};

export const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const { items, loading } = useChecklistItems();
  const [mounted, setMounted] = useState(false);
  const [minimised, setMinimised] = useState(false);
  const [dismissed, setDismissed] = useState<string[]>([]);

  useEffect(() => {
    setMounted(true);
    setMinimised(readMinimised());
    setDismissed(readDismissed());
  }, []);

  const completedIds = items.filter((i) => i.done).map((i) => i.id);
  const doneCount = completedIds.length;
  const total = items.length;
  const allDone = doneCount === total && total > 0;

  // Dismissed gating: the card stays hidden while the set of done
  // items is a SUPERSET of the dismissed set (meaning: nothing new
  // has appeared since the user dismissed). The moment a new item
  // becomes pending (or a new item is added in code), the set
  // shrinks vs dismissed and we re-show.
  const dismissedSnapshot = new Set(dismissed);
  const everyDoneWasDismissed = completedIds.every((id) =>
    dismissedSnapshot.has(id),
  );
  const everyPendingWasDismissed = items
    .filter((i) => !i.done)
    .every((i) => dismissedSnapshot.has(i.id));
  const userHasDismissed = dismissed.length > 0;
  const hiddenByDismissal =
    userHasDismissed && everyDoneWasDismissed && everyPendingWasDismissed;

  if (!mounted) return null;
  if (loading) return null;
  if (allDone && userHasDismissed) return null;
  if (hiddenByDismissal) return null;

  const handleItemClick = (item: ChecklistItem) => {
    navigate(item.to);
  };

  const handleDismiss = () => {
    const allIds = items.map((i) => i.id);
    writeDismissed(allIds);
    setDismissed(allIds);
  };

  const toggleMinimised = () => {
    const next = !minimised;
    setMinimised(next);
    writeMinimised(next);
  };

  return (
    <div
      role="dialog"
      aria-label="Onboarding checklist"
      aria-modal="false"
      className="fixed bottom-3 left-3 z-50 w-[min(360px,calc(100vw-1.5rem))] rounded-xl border border-white/10 bg-rook-home/95 shadow-xl backdrop-blur-md"
    >
      <header className="flex items-center justify-between gap-2 border-b border-white/10 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-rook-olive">
            Get set up
          </p>
          <p className="mt-0.5 text-sm font-medium text-rook-stone">
            {allDone ? "You're all set 🎉" : `${doneCount} of ${total} complete`}
          </p>
          <div
            className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-white/10"
            aria-hidden
          >
            <div
              className="h-full rounded-full bg-rook-purple transition-all"
              style={{ width: `${(doneCount / Math.max(total, 1)) * 100}%` }}
            />
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant={null}
            size={null}
            onClick={toggleMinimised}
            className="rounded p-1 text-rook-olive transition-colors hover:bg-white/10 hover:text-rook-stone"
            aria-label={minimised ? "Expand checklist" : "Minimise checklist"}
            title={minimised ? "Expand" : "Minimise"}
          >
            {minimised ? (
              <ChevronUp className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden />
            )}
          </Button>
          <Button
            type="button"
            variant={null}
            size={null}
            onClick={handleDismiss}
            className="rounded p-1 text-rook-olive transition-colors hover:bg-white/10 hover:text-rook-stone"
            aria-label="Dismiss checklist"
            title="Dismiss"
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </header>
      {!minimised && (
        <ul className="divide-y divide-white/[0.06]">
          {items.map((item) => (
            <li key={item.id}>
              <Button
                type="button"
                variant={null}
                size={null}
                onClick={() => handleItemClick(item)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-white/[0.04]"
              >
                {item.done ? (
                  <CheckCircle2
                    className="h-4 w-4 shrink-0 text-emerald-400"
                    aria-hidden
                  />
                ) : (
                  <Circle
                    className="h-4 w-4 shrink-0 text-rook-olive"
                    aria-hidden
                  />
                )}
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-sm ${item.done ? "text-rook-olive line-through" : "text-rook-stone"}`}
                  >
                    {item.title}
                  </p>
                  <p className="text-xs text-rook-olive">
                    {item.description}
                  </p>
                </div>
                <ChevronRight
                  className="h-4 w-4 shrink-0 text-rook-olive"
                  aria-hidden
                />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
